import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { Settings, GraduationCap, Map, ShieldCheck, ChevronRight, User } from 'lucide-react';
import SEO from '@/components/common/SEO';

// ─── Design tokens ────────────────────────────────────────────────────────────
const UNI_MIDNIGHT = '#06060c';
const UNI_DARK = '#0a0a14';
const UNI_GOLD = '#d4a843';
const UNI_INDIGO = '#0ea5e9';
const UNI_BORDER = 'rgba(255,255,255,0.07)';
const UNI_CARD = 'rgba(255,255,255,0.04)';
const UNI_TEXT = 'rgba(255,255,255,0.88)';
const TX = 'rgba(255,255,255,0.88)';
const UNI_GOLD_DIM = 'rgba(212,168,67,0.15)';
const UNI_MUTED = 'rgba(255,255,255,0.45)';

export default function SettingsPage() {
    const { user, userProfile, refreshProfile, isLoading: authLoading } = useAuth();
    const navigate = useNavigate();
    const { t } = useTranslation();

    const [saving, setSaving] = useState(false);
    const [success, setSuccess] = useState(false);
    const [isMobile, setIsMobile] = useState(false);

    useEffect(() => {
        const check = () => setIsMobile(window.innerWidth < 820);
        check();
        window.addEventListener('resize', check);
        return () => window.removeEventListener('resize', check);
    }, []);

    useEffect(() => {
        if (!authLoading && !user) navigate('/signin', { replace: true });
    }, [user, authLoading, navigate]);

    const currentMode = userProfile?.prep_pathway_mode || 'guided';

    async function updatePathway(mode: string) {
        if (saving) return;
        setSaving(true);
        setSuccess(false);

        try {
            const { error } = await supabase
                .from('users')
                .update({
                    prep_pathway_mode: mode,
                    prep_pathway_set_at: new Date().toISOString(),
                    prep_pathway_set_by: 'user'
                })
                .eq('id', user!.id);

            if (error) throw error;

            await refreshProfile();
            setSuccess(true);
            setTimeout(() => setSuccess(false), 3000);
        } catch (err) {
            console.error('Error updating pathway:', err);
        } finally {
            setSaving(false);
        }
    }

    const pathwayOptions = [
        {
            id: 'guided',
            title: 'Guided Pathway (Recommended)',
            desc: "Full preparation course shown first. Ideal for those new to university-level study.",
            icon: <GraduationCap size={20} />,
        },
        {
            id: 'bridge_only',
            title: 'Bridge Only',
            desc: "Skip foundational study skills and focus only on subject-specific theory bridges.",
            icon: <Map size={20} />,
        },
        {
            id: 'direct',
            title: 'Direct Entry',
            desc: "Hide all preparation content and go straight to the degree catalogue.",
            icon: <ShieldCheck size={20} />,
        }
    ];

    if (authLoading) return null;

    return (
        <div style={{
            background: UNI_MIDNIGHT, minHeight: '100vh', color: UNI_TEXT,
            fontFamily: "'Inter', sans-serif",
        }}>
            <SEO title="Settings" noindex />
            <div style={{ maxWidth: 800, margin: '0 auto', padding: isMobile ? '40px 20px 80px' : '60px 24px 80px' }}>

                {/* Header */}
                <div style={{ marginBottom: 44, display: 'flex', alignItems: 'center', gap: 16 }}>
                    <div style={{
                        width: 48, height: 48, borderRadius: 14, background: UNI_CARD,
                        display: 'flex', alignItems: 'center', justifyContent: 'center', color: UNI_GOLD,
                        border: `1px solid ${UNI_BORDER}`
                    }}>
                        <Settings size={24} />
                    </div>
                    <div>
                        <h1 style={{ fontSize: 28, fontWeight: 800, margin: 0, letterSpacing: '-0.5px' }}>Account Settings</h1>
                        <p style={{ color: UNI_MUTED, fontSize: 14, margin: '4px 0 0' }}>Manage your academic profile and learning experience.</p>
                    </div>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 40 }}>

                    {/* Profile Basic Info (Read Only for now) */}
                    <section>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
                            <User size={18} style={{ color: UNI_INDIGO }} />
                            <h2 style={{ fontSize: 18, fontWeight: 700, margin: 0 }}>Academic Identity</h2>
                        </div>

                        <div style={{
                            background: UNI_CARD, border: `1px solid ${UNI_BORDER}`, borderRadius: 20,
                            padding: '24px', display: 'flex', gap: 24, alignItems: 'center'
                        }}>
                            <div style={{
                                width: 64, height: 64, borderRadius: '50%', background: `linear-gradient(135deg, ${UNI_GOLD}, #b8912e)`,
                                display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24, fontWeight: 900, color: '#06060c'
                            }}>
                                {userProfile?.name?.charAt(0) || user?.email?.charAt(0).toUpperCase()}
                            </div>
                            <div style={{ flex: 1 }}>
                                <div style={{ fontSize: 18, fontWeight: 700, color: '#fff' }}>{userProfile?.name || 'Academic Learner'}</div>
                                <div style={{ fontSize: 13, color: UNI_MUTED }}>{user?.email}</div>
                                <div style={{ display: 'flex', gap: 12, marginTop: 8 }}>
                                    <span style={{
                                        fontSize: 10, fontWeight: 800, color: UNI_GOLD, background: UNI_GOLD_DIM,
                                        padding: '3px 10px', borderRadius: 100, border: '1px solid rgba(212,168,67,0.2)'
                                    }}>
                                        {userProfile?.active_plan?.toUpperCase() || 'FREE'}
                                    </span>
                                    {userProfile?.university_access && (
                                        <span style={{
                                            fontSize: 10, fontWeight: 800, color: '#4ade80', background: 'rgba(74,222,128,0.1)',
                                            padding: '3px 10px', borderRadius: 100, border: '1px solid rgba(74,222,128,0.2)'
                                        }}>
                                            UNIVERSITY ACCESS GRANTED
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>
                    </section>

                    {/* Learning Pathway Toggle */}
                    <section>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
                            <Map size={18} style={{ color: UNI_GOLD }} />
                            <h2 style={{ fontSize: 18, fontWeight: 700, margin: 0 }}>Learning Pathway</h2>
                        </div>

                        <p style={{ color: UNI_MUTED, fontSize: 14, lineHeight: 1.6, marginBottom: 20 }}>
                            Tailor your university experience by choosing how you want to progress.
                            Foundation subjects cover critical thinking and study skills, while bridge subjects prepare you for specific degree theory.
                        </p>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                            {pathwayOptions.map((opt) => {
                                const isActive = currentMode === opt.id;
                                return (
                                    <motion.button
                                        key={opt.id}
                                        whileHover={{ scale: 1.01, borderColor: isActive ? UNI_GOLD : 'rgba(255,255,255,0.2)' }}
                                        whileTap={{ scale: 0.99 }}
                                        onClick={() => updatePathway(opt.id)}
                                        disabled={saving}
                                        style={{
                                            display: 'flex', alignItems: 'flex-start', gap: 16,
                                            background: isActive ? 'rgba(212,168,67,0.08)' : UNI_CARD,
                                            border: `1.5px solid ${isActive ? UNI_GOLD : UNI_BORDER}`,
                                            borderRadius: 18, padding: '20px', textAlign: 'left',
                                            cursor: saving ? 'not-allowed' : 'pointer', transition: 'all 0.2s',
                                        }}
                                    >
                                        <div style={{
                                            width: 40, height: 40, borderRadius: 12, border: `1px solid ${isActive ? UNI_GOLD : UNI_BORDER}`,
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            color: isActive ? UNI_GOLD : UNI_MUTED, background: isActive ? 'rgba(212,168,67,0.1)' : 'transparent',
                                            flexShrink: 0,
                                        }}>
                                            {opt.icon}
                                        </div>
                                        <div style={{ flex: 1 }}>
                                            <div style={{ fontWeight: 700, fontSize: 16, color: isActive ? '#fff' : TX }}>{opt.title}</div>
                                            <div style={{ fontSize: 13, color: UNI_MUTED, marginTop: 4, lineHeight: 1.5 }}>{opt.desc}</div>
                                        </div>
                                        {isActive && (
                                            <div style={{ background: UNI_GOLD, color: '#000', padding: '4px 10px', borderRadius: 8, fontSize: 10, fontWeight: 800 }}>
                                                ACTIVE
                                            </div>
                                        )}
                                    </motion.button>
                                );
                            })}
                        </div>

                        {success && (
                            <motion.div
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                style={{ marginTop: 20, textAlign: 'center', color: '#4ade80', fontSize: 13, fontWeight: 600 }}
                            >
                                ✓ Pathway updated successfully
                            </motion.div>
                        )}
                    </section>

                    {/* Security & Access */}
                    <section>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
                            <ShieldCheck size={18} style={{ color: '#ef4444' }} />
                            <h2 style={{ fontSize: 18, fontWeight: 700, margin: 0 }}>Security & Access</h2>
                        </div>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                            <button
                                onClick={() => navigate('/reset-password')}
                                style={{
                                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                    background: UNI_CARD, border: `1px solid ${UNI_BORDER}`, borderRadius: 14,
                                    padding: '16px 20px', cursor: 'pointer', color: '#fff', fontSize: 14, fontWeight: 600
                                }}
                            >
                                Change Password <ChevronRight size={16} color={UNI_MUTED} />
                            </button>
                        </div>
                    </section>

                    {/* Footer Navigation */}
                    <div style={{ marginTop: 20, paddingTop: 32, borderTop: `1px solid ${UNI_BORDER}`, textAlign: 'center' }}>
                        <button
                            onClick={() => navigate('/dashboard')}
                            style={{ background: 'none', border: 'none', color: UNI_GOLD, fontSize: 14, fontWeight: 700, cursor: 'pointer' }}
                        >
                            ← Back to Dashboard
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}


